/**
 * Login Page - B2C email/password login
 * Split layout: full-white left panel + marketing right
 */

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { authApi } from "../../services/authApi";
import { captureAppEvent } from "../../lib/posthogTracking";
import { useLang } from "../../shared/i18n";
import AuthSplitShell from "./AuthSplitShell";
import MicrosoftIcon from "./MicrosoftIcon";
import GoogleIcon from "./GoogleIcon";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLang();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate("/dashboard");
    } else {
      if (result.code === "NOT_VERIFIED") {
        captureAppEvent("user_login_failed", { reason_code: "not_verified" });
        navigate(`/verify?email=${encodeURIComponent(email)}`);
      } else {
        captureAppEvent("user_login_failed", {
          reason_code: result.code || "invalid_credentials",
        });
        setError(result.message || t("loginFailed"));
      }
    }
    setLoading(false);
  };

  const handleSsoLogin = async (provider: "microsoft" | "google") => {
    setSsoLoading(provider);
    setError("");
    const result =
      provider === "microsoft"
        ? await authApi.microsoftLogin()
        : await authApi.googleLogin();
    if (result.success && result.data?.authorization_url) {
      captureAppEvent("user_sso_redirected", { provider, page: "login" });
      window.location.href = result.data.authorization_url;
    } else {
      setError(result.message || t("ssoFailed"));
      setSsoLoading(null);
    }
  };

  return (
    <AuthSplitShell>
      <h1>{t("loginTitle")}</h1>
      <p className="auth-panel__subtitle">{t("loginSubtitle")}</p>
      <form onSubmit={handleSubmit} className="auth-form">
        {error && <div className="auth-error">{error}</div>}
        <div className="form-group">
          <label htmlFor="email">{t("emailLabel")}</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("emailPlaceholder")}
            required
            autoFocus
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">{t("passwordLabel")}</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("passwordPlaceholder")}
            required
          />
        </div>
        <div className="auth-links-inline">
          <Link to="/forgot-password">{t("forgotPassword")}</Link>
        </div>
        <button type="submit" className="auth-btn" disabled={loading}>
          {loading ? t("loginLoading") : t("loginButton")}
        </button>
      </form>
      <div className="auth-divider">
        <span>{t("orDivider")}</span>
      </div>
      <div className="auth-sso-buttons">
        <button
          type="button"
          className="auth-btn-sso"
          onClick={() => handleSsoLogin("google")}
          disabled={!!ssoLoading}
        >
          <GoogleIcon />{" "}
          {ssoLoading === "google" ? t("redirecting") : t("googleLogin")}
        </button>
        <button
          type="button"
          className="auth-btn-sso"
          onClick={() => handleSsoLogin("microsoft")}
          disabled={!!ssoLoading}
        >
          <MicrosoftIcon />{" "}
          {ssoLoading === "microsoft" ? t("redirecting") : t("microsoftLogin")}
        </button>
      </div>
      <div className="auth-footer">
        <span>{t("noAccount")} </span>
        <Link to="/register">{t("createAccount")}</Link>
      </div>
    </AuthSplitShell>
  );
};

export default LoginPage;
